import { useMutation, useQueryClient } from '@tanstack/react-query';
import LocationService, {
  ICreateLocationParams,
} from '@clickersphere/core/services/LocationService';
import { useBuildQueryKey } from '../useBuildQueryKey';
import { LOCATIONS_QUERY_KEY, LocationQueryData } from './useLocations';

export function useCreateLocation() {
  const queryClient = useQueryClient();
  const { buildQueryKey } = useBuildQueryKey();

  const {
    isPending: createLocationIsPending,
    mutateAsync: createLocationAsync,
  } = useMutation({
    mutationFn: async (data: ICreateLocationParams) => {
      const location = await LocationService.create(data);
      return location as LocationQueryData;
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({
        queryKey: buildQueryKey(LOCATIONS_QUERY_KEY),
      });

      const tempId = String(crypto.randomUUID());

      queryClient.setQueryData<LocationQueryData[]>(
        buildQueryKey(LOCATIONS_QUERY_KEY),
        (old) => [
          ...(old ?? []),
          {
            id: tempId,
            name: variables.name,
            address: variables.address ?? null,
            queryStatus: 'pending',
          },
        ],
      );

      return { tempId };
    },

    onSuccess: async (data, _variables, context) => {
      await queryClient.cancelQueries({
        queryKey: buildQueryKey(LOCATIONS_QUERY_KEY),
      });

      queryClient.setQueryData<LocationQueryData[]>(
        buildQueryKey(LOCATIONS_QUERY_KEY),
        (old) => {
          const withoutTemp = (old ?? []).filter(
            (location) => location.id !== context.tempId,
          );
          const alreadyThere = withoutTemp.some(
            (location) => location.id === data.id,
          );

          return alreadyThere ? withoutTemp : [...withoutTemp, data];
        },
      );
    },

    onError: async (_error, _variables, context) => {
      await queryClient.cancelQueries({
        queryKey: buildQueryKey(LOCATIONS_QUERY_KEY),
      });

      queryClient.setQueryData<LocationQueryData[]>(
        buildQueryKey(LOCATIONS_QUERY_KEY),
        (old) => old?.filter((location) => location.id !== context?.tempId),
      );
    },
  });

  return {
    createLocationAsync,
    createLocationIsPending,
  };
}
